<template>
  <div class="content">
    <a-row>
      <a-col :span="12">
        <a-button :class="type == 'all' ? 'select-btn' : ''"
                  @click="searchClick('all')">{{ $t('Notice.NoticeUnread.all') }}</a-button>
        <a-button :class="type == 'order-info' ? 'select-btn' : ''"
                  @click="searchClick('order-info')">{{ $t('Notice.NoticeUnread.orderNotification') }}</a-button>
        <a-button :class="type == 'comment-info' ? 'select-btn' : ''"
                  @click="searchClick('comment-info')">{{ $t('Notice.NoticeUnread.commentReply') }}</a-button>
      </a-col>
      <a-col :span="12"
             class="headerGroup">
        <a-button @click="ButtonFunc.read">
          <container-outlined />{{ $t('Notice.NoticeUnread.markAsRead') }}
        </a-button>
        <a-button @click="ButtonFunc.allRead">
          <file-done-outlined />{{ $t('Notice.NoticeUnread.markAllAsRead') }}
        </a-button>
        <a-button type="danger"
                  @click="ButtonFunc.delete">
          <delete-outlined />{{ $t('Notice.NoticeUnread.delete') }}</a-button>
      </a-col>
    </a-row>
    <a-table class="table"
             :data-source="noticeList"
             :row-selection="{ selectedRowKeys: tableSelectionKeys, onChange: tableSelectionOnchange, type: 'checkbox' }"
             :pagination="false"
             row-key="id"
             :scroll="{ y: '600px', x: '100%' }"
             :columns="tableColumns">
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'content'">
          <div v-if="record.type == 'comment-info'">
            {{ JSON.parse(record.content).content }}
          </div>
        </template>
        <template v-if="column.key === 'createdAt'">
          <div>{{ convertTime(record.createdAt) }}</div>
        </template>
        <template v-if="column.key === 'operation'">
          <div v-if="record.type == 'comment-info'"
               class="detail-btn"
               @click="goToDetail(record)">{{ $t('Notice.NoticeUnread.viewDetails') }}</div>
        </template>
      </template>
    </a-table>
    <a-row>
      <a-col :span="24"
             class="footerPagination">
        <APagination v-model:current="paginationLimit"
                     :total="paginationTotal"
                     :show-size-changer="true"
                     @change="changeNoticePagination" />
      </a-col>
    </a-row>
  </div>
</template>


<script setup lang="ts">
import { APagination } from '@iota-cn/ui-components'
import { message } from 'ant-design-vue'
import { onMounted, reactive, ref, computed } from 'vue'
import { convertTime } from '../plugin'
import { noticeDelete, noticeGetList, noticeSeed } from '../service/notice'
import type { NoticeData } from '../service/type'
import { useRouter } from "vue-router"
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()
const paginationLimit = ref<number>(1)
const paginationTotal = ref<number>(0)
const pagesize = ref<number>(10)
const tableSelectionKeys = ref<string[]>([])
const tableSelectionRows = ref<Array<NoticeData>>([])

// declared code
onMounted(() => {
  const noticeData = sessionStorage.getItem('noticeData') as any
  const data = JSON.parse(noticeData)
  if (data) {
    type.value = data.type
    paginationLimit.value = data.offset
    getNoticeList(data.limit, (data.offset - 1) * data.limit)
  } else {
    getNoticeList()
  }
  sessionStorage.clear()
})
const type = ref<string>('all')
const searchClick = (title: string) => {
  type.value = title
  paginationLimit.value = 1
  const data = {
    limit: 10,
    offset: 0,
    status: '2',
  } as any
  if (type.value != 'all') {
    data.type = type.value
  }
  noticeGetList(data).then(result => {
    noticeList.value = result.rows
    paginationTotal.value = result.total
  })
}
const goToDetail = (record: any) => {
  const comment_id = JSON.parse(record.content).id
  const data = {
    type: type.value,
    offset: paginationLimit.value,
    limit: pagesize.value
  }
  sessionStorage.setItem('noticeData', JSON.stringify(data))
  router.push(`/console/accounts/comment/${comment_id}?from=comment`)
  noticeSeed({ noticeIds: [record.id] })
}
// declared static
const noticeList = ref<Array<NoticeData>>([])

const tableColumns = computed(() => [
  { title: t('Notice.NoticeUnread.notificationTitle'), dataIndex: "title", key: "title", width: '25%', align: 'center' },
  { title: t('Notice.NoticeUnread.notificationMessage'), dataIndex: "content", key: "content", width: '50%', align: 'center' },
  { title: t('Notice.NoticeUnread.notificationTime'), dataIndex: "createdAt", key: "createdAt", width: '25%', align: 'center' },
  { title: t('Notice.NoticeUnread.operation'), dataIndex: "operation", key: "operation", width: '15%', align: 'center' },
])

// declared Method
const tableSelectionOnchange = (keys: string[], rows: Array<NoticeData>) => {
  tableSelectionKeys.value = keys
  tableSelectionRows.value = rows
}

const changeNoticePagination = (page: number, pageSize: number) => {
  paginationLimit.value = page
  pagesize.value = pageSize
  getNoticeList(pageSize, (page - 1) * pageSize)
}

const getNoticeList = (limit = 10, offset = 0, status = '2') => {
  const data = {
    limit,
    offset,
    status,
  } as any
  if (type.value != 'all') {
    data.type = type.value
  }
  noticeGetList(data).then(result => {
    noticeList.value = result.rows
    paginationTotal.value = result.total
  })
}

const ButtonFunc = {
  delete: () => {
    const noticeIds = tableSelectionRows.value.map(item => item.id)

    noticeDelete(noticeIds.join(',')).then(() => {
      message.success(t('Notice.NoticeUnread.deleteSuccess'))
      getNoticeList()
    })
  },
  /** 删除全部消息 */
  allDelete: () => {
    noticeDelete().then(() => {
      message.success(t('Notice.NoticeUnread.deleteAllSuccess'))
      getNoticeList()
    })
  },
  /** 已读一条消息 */
  read: () => {
    if (tableSelectionRows.value.length === 0) {
      message.warning(t('Notice.NoticeUnread.selectMessage'))
      return
    }
    const id = tableSelectionRows.value.map(item => item.id)
    noticeSeed({ noticeIds: id }).then(() => {
      message.success(t('Notice.NoticeUnread.markReadSuccess'))
      getNoticeList()
    })
  },
  /** 已读全部消息 */
  allRead: () => {
    noticeSeed({ noticeIds: [] }).then(() => {
      message.success(t('Notice.NoticeUnread.markAllReadSuccess'))
      getNoticeList()
    })
  }
}

</script>


<style lang="less" scoped>
.content {
  width: 100%;
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;

  .select-btn {
    background-color: #111;
    color: #fff;
  }

  .table {
    width: 100%;
    flex: 1;
    overflow: auto;

    .detail-btn {
      cursor: pointer;
    }
  }

  .headerGroup {
    display: flex;
    justify-content: end;
    margin-bottom: 20px;

    .ant-btn {
      margin: 0 10px 0 0
    }
  }

  .footerPagination {
    display: flex;
    justify-content: end;
  }
}
</style>
